import { useState, useEffect, useRef } from "react";
import { cardImage } from "@/lib/img";

// Self-healing image: retries transient load failures (requests aborted or
// throttled during heavy concurrent loads) before showing a graceful fallback,
// so a one-off network hiccup never leaves a permanently broken image.
export default function SmartImage({ src, alt = "", className = "", children, fallbackSrc, width = 600, priority = false, hideOnFail = false, ...rest }) {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    setAttempt(0);
    setFailed(false);
    setUseFallback(false);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [src]);

  useEffect(() => {
    // swap to the fallback photo instead of a blank placeholder
    if ((!src || failed) && fallbackSrc && !useFallback) { setUseFallback(true); setFailed(false); setAttempt(0); }
  }, [src, failed, fallbackSrc, useFallback]);
  const raw = useFallback ? fallbackSrc : src;
  // proxied copy first; on the final retry fall back to the original URL
  const realSrc = raw && attempt < 2 ? cardImage(raw, width) : raw;
  if (!realSrc || failed) {
    if (hideOnFail) return null;
    return (
      <div className={`${className} flex items-center justify-center bg-gradient-to-br from-neutral-100 to-neutral-200 text-neutral-300`}>
        {children}
      </div>
    );
  }

  const onError = () => {
    if (attempt < 3) {
      timer.current = setTimeout(() => setAttempt((a) => a + 1), 600 * (attempt + 1));
    } else {
      setFailed(true);
    }
  };

  // key={attempt} forces a brand-new <img> node on retry so the browser re-requests.
  const bust = attempt > 0 ? (realSrc.includes("?") ? "&" : "?") + "_r=" + attempt : "";

  return (
    <img
      key={attempt}
      src={realSrc + bust}
      alt={alt}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : "auto"}
      decoding="async"
      referrerPolicy="no-referrer"
      onError={onError}
      className={className}
      {...rest}
    />
  );
}
